// "Over the Road" sentinel — double-underscore prefix avoids collision with
// real group ids; non-empty so URL round-trips keep it.
export const OTR_SHOP_VALUE = '__OTR__';

// Macro matching any asset bound to a shop. Sentinel rather than expanded
// list so future shop assignments auto-include without re-saving the filter.
export const IN_MY_SHOPS_VALUE = '__IN_MY_SHOPS__';

// Matches assets with no home-location group binding (locationGroups empty).
export const UNASSIGNED_HOME_VALUE = '__UNASSIGNED_HOME__';

// Status-mode (S0·H≥1) "in any geofence" sentinel, vs membership-scoped IN_MY_SHOPS_VALUE.
export const IN_ANY_SHOP_VALUE = '__IN_ANY_SHOP__';

// The two shop sentinels resolved against shop membership rather than a single
// geofence id. OTR is not a macro — it's the no-geofence complement, matched
// client-side like a concrete id.
export const isShopMacro = (value: string): boolean =>
  value === IN_MY_SHOPS_VALUE || value === IN_ANY_SHOP_VALUE;

// Concrete geofence ids in a shop selection — neither a macro nor OTR.
export const concreteShopIds = (shopLocations: readonly string[] | undefined): string[] =>
  (shopLocations ?? []).filter(s => !isShopMacro(s) && s !== OTR_SHOP_VALUE);

import type { ParamMap } from '@angular/router';
import { FeatureSettings, TenantFeatures, isFeatureEnabled } from 'src/app/models/featureSettings';
import { scFlagOverrides } from '../sc-cache-shared';
import { activeDistance, DistanceAnchor, DistanceUnit } from '../distance-filter';

const featureOn = (features: FeatureSettings | null, flag: TenantFeatures): boolean =>
  isFeatureEnabled(features, flag) || scFlagOverrides().has(flag);

export const distanceFilterEnabled = (features: FeatureSettings | null): boolean =>
  featureOn(features, TenantFeatures.ServiceCenterDistanceFilter);

const GATED_URL_KEYS: Record<GatedUrlFacet, readonly string[]> = {
  dvir: ['dvir'],
  distance: ['near', 'radius', 'unit', 'nearSource'],
};

export type GatedUrlFacet = 'dvir' | 'distance';
export type GatedUrlFacets = Record<GatedUrlFacet, boolean>;

const GATED_FACETS = Object.keys(GATED_URL_KEYS) as GatedUrlFacet[];

export const FILTER_URL_KEYS: readonly string[] = ([] as string[]).concat(
  ['home', 'shop', 'name', 'status', 'type', 'severity'],
  ...GATED_FACETS.map(facet => GATED_URL_KEYS[facet]));

// A facet the tenant lacks has no params at all, so a link carrying only those cannot make the
// URL canonical and read every other facet from absent params.
export const gatedParams = (qp: ParamMap, enabled: GatedUrlFacets): ParamMap => {
  const hidden = new Set<string>();
  for (const facet of GATED_FACETS) {
    if (!enabled[facet]) GATED_URL_KEYS[facet].forEach(key => hidden.add(key));
  }
  if (hidden.size === 0) return qp;
  return {
    get keys() { return qp.keys.filter(k => !hidden.has(k)); },
    has: (k: string) => !hidden.has(k) && qp.has(k),
    get: (k: string) => hidden.has(k) ? null : qp.get(k),
    getAll: (k: string) => hidden.has(k) ? [] : qp.getAll(k),
  };
};

export interface ServiceCenterUserType {
  shopCount: number;
  homeCount: number;
}

export interface ViewportFilterBounds {
  north: number;
  south: number;
  east:  number;
  west:  number;
}

export interface ServiceCenterFilter {
  // Matches name OR vin (client-side, over the cached universe).
  nameSearch?: string;
  groupNameSearch?: string;
  // Matches severity reason descriptions. Client-side only — never sent to the
  // server; location aggregates re-derive it from the warm asset universe.
  reasonSearch?: string;
  serviceStatus?: number | null;
  groupId?: string;
  severities: string[];
  assetTypes: string[];
  make?: string;
  model?: string;
  modelYearMin?: number;
  modelYearMax?: number;
  weightRatings?: string[];
  // Single checkbox: unchecked → undefined, checked → true (no DVIR-clear case).
  dvirWithDefects?: boolean;
  homeLocationGroupIds?: string[];
  shopLocations?: string[];
  // Static bounds — does not track pan/zoom; cleared via Clear All Filters or the filter-bar chip.
  viewportFilter?: ViewportFilterBounds;
  near?: DistanceAnchor;
  radius?: number;
  unit?: DistanceUnit;
  offset: number;
  limit: number;
}

const sameValueSet = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every(v => setB.has(v));
};

// Gates auto-drill / tier shortcuts. Shop/home compare against the type-default
// (not empty), so an affixed-only filter reads as inactive; no type → empty default.
export const isFilterActive = (
  filter: ServiceCenterFilter,
  type: ServiceCenterUserType | null,
  shopGroups: ReadonlyArray<{ id: string }>,
  homeGroups: ReadonlyArray<{ id: string }>,
): boolean => {
  const def = applyTypeDefaults(defaultFilter(), type, shopGroups, homeGroups);
  return !!(
    filter.nameSearch ||
    filter.groupNameSearch ||
    filter.reasonSearch ||
    filter.serviceStatus != null ||
    filter.groupId ||
    filter.severities?.length ||
    filter.assetTypes?.length ||
    filter.make ||
    filter.model ||
    filter.modelYearMin != null ||
    filter.modelYearMax != null ||
    filter.weightRatings?.length ||
    filter.dvirWithDefects ||
    !sameValueSet(filter.homeLocationGroupIds ?? [], def.homeLocationGroupIds ?? []) ||
    !sameValueSet(filter.shopLocations ?? [], def.shopLocations ?? []) ||
    filter.viewportFilter || activeDistance(filter)
  );
};

export const defaultFilter = (): ServiceCenterFilter => ({
  nameSearch: undefined,
  groupNameSearch: undefined,
  reasonSearch: undefined,
  serviceStatus: null,
  groupId: undefined,
  severities: [],
  assetTypes: [],
  make: undefined,
  model: undefined,
  modelYearMin: undefined,
  modelYearMax: undefined,
  weightRatings: [],
  dvirWithDefects: undefined,
  homeLocationGroupIds: [],
  shopLocations: [],
  offset: 0,
  limit: 10,
});

const shopValueValidForType = (value: string, shopCount: number, homeCount: number): boolean => {
  if (value === IN_MY_SHOPS_VALUE) return shopCount >= 2;
  if (value === IN_ANY_SHOP_VALUE) return shopCount === 0 && homeCount >= 1;
  if (value === OTR_SHOP_VALUE) return homeCount >= 1;
  return shopCount >= 1;
};

const homeValueValidForType = (value: string, shopCount: number, homeCount: number): boolean => {
  if (value === UNASSIGNED_HOME_VALUE) return shopCount >= 1 && homeCount >= 1;
  return homeCount >= 1;
};

// A single shop is a hard scope only with no home assets. Home is never locked:
// membership already bounds the universe, so a forced home filter could only
// hide yard visitors from other regions.
export const isShopAffixed = (shopCount: number, homeCount: number): boolean =>
  shopCount === 1 && homeCount === 0;

// Ordered current-location / home options for a type — the shop macro first
// (IN_MY_SHOPS / IN_ANY_SHOP are mutually exclusive, so only one survives),
// then OTR, then the real groups. The affixed shop renders a lock chip instead
// and never reaches these.
export const shopOptionValues = (shopCount: number, homeCount: number, shopIds: string[]): string[] =>
  [IN_MY_SHOPS_VALUE, IN_ANY_SHOP_VALUE, OTR_SHOP_VALUE, ...shopIds]
    .filter(v => shopValueValidForType(v, shopCount, homeCount));
export const homeOptionValues = (shopCount: number, homeCount: number, homeIds: string[]): string[] =>
  [UNASSIGNED_HOME_VALUE, ...homeIds]
    .filter(v => homeValueValidForType(v, shopCount, homeCount));

// Single reconcile; null type is a no-op. The affixed shop overwrites the array
// with the lone group id (wins even over a ?shop= deep link); home sanitises-but-keeps.
export const applyTypeDefaults = (
  filter: ServiceCenterFilter,
  type: ServiceCenterUserType | null,
  shopGroups: ReadonlyArray<{ id: string }> = [],
  homeGroups: ReadonlyArray<{ id: string }> = [],
): ServiceCenterFilter => {
  if (!type) return filter;
  const { shopCount, homeCount } = type;

  const affixShop = isShopAffixed(shopCount, homeCount) && !!shopGroups[0];
  const shopLocations = affixShop
    ? [shopGroups[0].id]
    : (filter.shopLocations ?? []).filter(v => shopValueValidForType(v, shopCount, homeCount));

  const homeLocationGroupIds = (filter.homeLocationGroupIds ?? [])
    .filter(v => homeValueValidForType(v, shopCount, homeCount));

  return { ...filter, shopLocations, homeLocationGroupIds };
};

export type ServiceCenterCardKind = 'assets' | 'locations';

export interface EmptyStateCopy {
  icon: string;
  titleKey: string;
  hintKey: string;
}

const NO_DATA_EMPTY_STATE: EmptyStateCopy = {
  icon: 'inbox',
  titleKey: '@noServiceCenterData',
  hintKey: '@noServiceCenterDataHint',
};

const EMPTY_STATE: Record<ServiceCenterCardKind, { filtered: EmptyStateCopy; neutral: EmptyStateCopy }> = {
  assets: {
    filtered: { icon: 'filter_alt_off', titleKey: '@noAssetsMatchFilter', hintKey: '@tryClearingFilterOrLocation' },
    neutral: { icon: 'local_shipping', titleKey: '@noAssetsRightNow', hintKey: '@noAssetsRightNowHint' },
  },
  locations: {
    filtered: { icon: 'filter_alt_off', titleKey: '@noLocationsMatchFilter', hintKey: '@tryClearingFilter' },
    neutral: { icon: 'warehouse', titleKey: '@noLocationsRightNow', hintKey: '@noLocationsRightNowHint' },
  },
};

// S0·H0 has no groups and no data — distinct from a filtered-to-empty or
// assigned-but-quiet view (decision #7).
export const emptyStateCopy = (
  kind: ServiceCenterCardKind,
  type: ServiceCenterUserType | null,
  filterActive: boolean,
): EmptyStateCopy => {
  if (type?.shopCount === 0 && type?.homeCount === 0) return NO_DATA_EMPTY_STATE;
  return filterActive ? EMPTY_STATE[kind].filtered : EMPTY_STATE[kind].neutral;
};
